import React, { useState, useEffect, useRef } from "react";
import { format, isValid } from "date-fns";
import { mdiFilterVariant, mdiClose, mdiPlus, mdiDownload } from "@mdi/js";
import {
  cn,
  getBackgroundStyles,
  getBorderStyles,
  getFocusRingStyles,
  incPanelColorLevel,
} from "../../../styles";
import { Button, ButtonVariant } from "../../../atoms/Button";
import { Badge } from "../../../atoms/Badge";
import { Chip } from "../../../molecules/Chip";
import { Select, SelectOption } from "../../../molecules/Select";
import { Input } from "../../../molecules/Input";
import { useDataTableContext } from "../context";
import { FILTER_OPERATORS } from "../constants";
import { ColumnType } from "../types";
import { FilterInput } from "./FilterInput";
import {
  hasFilterValue,
  type ColumnFilter,
} from "../hooks/useFiltering";
import { ThemeColor } from "../../../props";
import { Typography, TypographyContent } from "../../../atoms/Typography";
import { ScrollArea } from "../../../layouts/ScrollArea";
import { v4 as uuid } from "uuid";

export interface ToolbarProps {
  className?: string;
  defaultFilterExpanded?: boolean;
  filterOnlyToolbar?: boolean;
}

export function Toolbar({
  className,
  defaultFilterExpanded = false,
  filterOnlyToolbar = false,
}: ToolbarProps) {
  const {
    table,
    color,
    filtering,
    dense,
    selection,
    renderBatchActions,
    renderToolbarActions,
    onDownload,
  } = useDataTableContext();

  const [filterExpanded, setFilterExpanded] = useState(defaultFilterExpanded);
  const [draftFilter, setDraftFilter] = useState<ColumnFilter>(() => ({
    id: uuid(),
    columnId:
      table.columns.find((column) => column.filterable && !column.hidden)?.id ??
      table.columns[0]?.id ??
      "",
    operator: "=",
    value: "",
  }));
  const columnSelectRef = useRef<any>(null);
  const isFilterExpanded = filterOnlyToolbar || filterExpanded;

  useEffect(() => {
    if (isFilterExpanded) columnSelectRef.current?.focus();
  }, [isFilterExpanded]);

  const selectedRows = selection?.selected ?? [];
  const hasSelection = selectedRows.length > 0;

  const filterableColumns = table.columns.filter(
    (col) => col.filterable && !col.hidden,
  );
  const hasFiltering = !!filtering && filterableColumns.length > 0;
  const activeFilters =
    filtering?.filters.filter((filter) => hasFilterValue(filter.value)) ?? [];
  const chipCount = activeFilters.length;

  const toolbarActions = renderToolbarActions?.(table);
  const hasToolbarActions = Array.isArray(toolbarActions)
    ? toolbarActions.length > 0
    : !!toolbarActions;

  // chips need their own row once something else claims the top row
  const chipsBelow = !!renderBatchActions || hasToolbarActions;

  const hasSelectionCapability = !!selection;
  if (filterOnlyToolbar && !hasFiltering) return null;
  if (
    !hasFiltering &&
    !hasSelectionCapability &&
    !onDownload &&
    !hasToolbarActions
  )
    return null;

  // Filter input setup
  const activeColumn = table.columns.find((c) => c.id === draftFilter.columnId);
  const defaultOperators = activeColumn
    ? (FILTER_OPERATORS[activeColumn.type] ?? FILTER_OPERATORS[ColumnType.TEXT])
    : FILTER_OPERATORS[ColumnType.TEXT];
  const operators =
    (activeColumn && filtering?.operatorOptions?.(activeColumn)) ??
    defaultOperators;

  const columnOptions: SelectOption[] = filterableColumns.map((col) => ({
    value: col.id,
    label: col.label,
  }));

  const operatorOptions: SelectOption[] = operators.map((op) => ({
    value: op.id,
    label: op.label,
    iconPath: op.iconPath,
  }));

  const handleColumnChange = (option: SelectOption | SelectOption[]) => {
    if (Array.isArray(option) || !filtering) return;
    const newColumnId = option?.value as string;
    if (!newColumnId) return;

    const col = table.columns.find((c) => c.id === newColumnId);
    const defaultOps = col
      ? (FILTER_OPERATORS[col.type] ?? FILTER_OPERATORS[ColumnType.TEXT])
      : [];
    const ops = (col && filtering.operatorOptions?.(col)) ?? defaultOps;
    setDraftFilter({
      id: uuid(),
      columnId: newColumnId,
      operator: ops[0]?.id ?? "=",
      value: "",
    });
  };

  const handleOperatorChange = (option: SelectOption | SelectOption[]) => {
    if (Array.isArray(option) || !filtering) return;
    const newOperator = option?.value as string;
    if (newOperator) {
      setDraftFilter((current) => ({ ...current, operator: newOperator }));
    }
  };

  const handleSubmit = () => {
    if (filtering?.addFilter(draftFilter)) {
      setDraftFilter((current) => ({
        ...current,
        id: uuid(),
        value: "",
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSubmit();
  };

  const selectedColumnOption =
    columnOptions.find((o) => o.value === draftFilter.columnId) ?? null;
  const selectedOperatorOption =
    operatorOptions.find((o) => o.value === draftFilter.operator) ?? null;

  const renderChips = () => {
    if (!filtering || chipCount === 0) return null;
    return activeFilters.map((chip) => {
      const column = table.columns.find((c) => c.id === chip.columnId);
      const defaultOps =
        FILTER_OPERATORS[column?.type ?? ColumnType.TEXT] ??
        FILTER_OPERATORS[ColumnType.TEXT];
      const ops = (column && filtering.operatorOptions?.(column)) ?? defaultOps;
      const op = ops.find((o) => o.id === chip.operator);

      const hasOperators = ops.length > 0;
      const operatorLabel: TypographyContent | null = hasOperators
        ? (op?.label ?? chip.operator)
        : null;

      const defaultChipValue = (() => {
        const v = chip.value;
        if (v == null) return "";
        if (v instanceof Date && isValid(v)) {
          const fmt =
            column?.type === ColumnType.DATETIME
              ? "dd.MM.yyyy HH:mm"
              : "dd.MM.yyyy";
          return format(v, fmt);
        }
        if (v === "true") return "Yes";
        if (v === "false") return "No";
        return String(v);
      })();
      const chipValueContent = (column &&
        filtering.renderChipValue?.(column, chip.value, chip.operator)) ?? (
        <span>&quot;{defaultChipValue}&quot;</span>
      );

      return (
        <Chip
          key={chip.id}
          onAction={() => filtering.removeFilter(chip.id)}
          dense
        >
          <Typography.Span
            className="text-front-400 uppercase tracking-tighter"
            content={column?.label ?? chip.columnId}
          />
          {operatorLabel != null && (
            <Typography.Span
              className="text-primary-500 mx-1"
              content={operatorLabel}
            />
          )}
          <span className="text-front-800 dark:text-front-200">
            {chipValueContent}
          </span>
        </Chip>
      );
    });
  };

  return (
    <div
      className={cn("border-b border-back-200 dark:border-back-700", className)}
    >
      {/* Top row: filter toggle + chips (collapsed) | batch actions */}
      {!filterOnlyToolbar && (
        <div
          className={cn(
            "flex items-center justify-between min-h-[44px] gap-4",
            dense ? "px-1.5 py-1" : "px-3 py-2",
          )}
        >
          {hasSelection && renderBatchActions && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] h-[30px] flex items-center justify-center font-bold uppercase text-primary-600 bg-primary-100 dark:bg-primary-900 px-2 rounded">
                {selectedRows.length}
                <Typography.Span className="ml-1" local>
                  Selected
                </Typography.Span>
              </span>
              {renderBatchActions(selectedRows, () => selection?.deselectAll())}
              <Button
                variant={ButtonVariant.text}
                onClick={() => selection?.deselectAll()}
                iconPath={mdiClose}
                dense
              />
            </div>
          )}

          <ScrollArea
            direction="horizontal"
            className="flex-1 min-w-0"
            contentClassName="flex items-center gap-2"
          >
            {hasFiltering && !chipsBelow && renderChips()}
            {toolbarActions}
          </ScrollArea>

          {hasSelection && !renderBatchActions && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] h-[30px] flex items-center justify-center  font-bold uppercase text-primary-600 bg-primary-100 dark:bg-primary-900 px-2 py-1 rounded">
                {selectedRows.length}
                <Typography.Span className="ml-1" local>
                  Selected
                </Typography.Span>
              </span>
              <Button
                variant={ButtonVariant.text}
                onClick={() => selection?.deselectAll()}
                iconPath={mdiClose}
                dense
              />
            </div>
          )}

          {(hasFiltering || onDownload) && (
            <div className="flex items-center gap-2 pl-4 border-l border-back-300 dark:border-back-600">
              {hasFiltering && (
                <Badge
                  content={chipCount || null}
                  color={ThemeColor.primary}
                  badgeClassName="m-1"
                >
                  <Button
                    variant={ButtonVariant.text}
                    iconPath={mdiFilterVariant}
                    onClick={() => setFilterExpanded((v) => !v)}
                    dense
                  >
                    <Typography.Span local>Filter</Typography.Span>
                  </Button>
                </Badge>
              )}
              {onDownload && (
                <Button
                  variant={ButtonVariant.text}
                  iconPath={mdiDownload}
                  onClick={onDownload}
                  dense
                >
                  <Typography.Span local>Download</Typography.Span>
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Expanded filter input row */}
      {isFilterExpanded && hasFiltering && (
        <div
          className={cn(
            "flex flex-col gap-2",
            !filterOnlyToolbar &&
              "border-t border-back-100 dark:border-back-800",
            dense ? "p-1.5" : "p-3",
          )}
        >
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "flex items-center rounded border divide-x",
                "focus-within:outline-none focus-within:ring-2",
                getFocusRingStyles(ThemeColor.primary),
                getBackgroundStyles(incPanelColorLevel(color)),
                getBorderStyles(incPanelColorLevel(color)),
                "divide-back-200 dark:divide-back-700",
              )}
            >
              <Select
                ref={columnSelectRef}
                options={columnOptions}
                value={selectedColumnOption}
                onChange={handleColumnChange}
                dense
                clearable={false}
                frameless
                className="min-w-30"
              />
              {operatorOptions.length > 0 && (
                <Select
                  options={operatorOptions}
                  value={selectedOperatorOption}
                  onChange={handleOperatorChange}
                  dense
                  clearable={false}
                  frameless
                  hideIndicator
                  iconOnly
                  tint={ThemeColor.primary}
                />
              )}
            </div>
            <div className="flex-1 min-w-[200px]">
              {(() => {
                const customInput =
                  activeColumn &&
                  filtering.renderInput?.(activeColumn, {
                    value: draftFilter.value,
                    onChange: (value) =>
                      setDraftFilter((current) => ({ ...current, value })),
                    onSubmit: handleSubmit,
                  });
                if (customInput) return customInput;
                if (activeColumn) {
                  return (
                    <FilterInput
                      column={activeColumn}
                      value={draftFilter.value}
                      operator={draftFilter.operator}
                      onChange={(value) =>
                        setDraftFilter((current) => ({ ...current, value }))
                      }
                      onSubmit={handleSubmit}
                    />
                  );
                }
                return (
                  <Input
                    value={
                      typeof draftFilter.value === "string"
                        ? draftFilter.value
                        : null
                    }
                    onChange={(value) =>
                      setDraftFilter((current) => ({ ...current, value }))
                    }
                    onKeyDown={handleKeyDown}
                    placeholder="Search..."
                    dense
                  />
                );
              })()}
            </div>
            <Button
              variant={ButtonVariant.contained}
              disabled={draftFilter.value == null || draftFilter.value === ""}
              onClick={handleSubmit}
              iconPath={mdiPlus}
              dense
            >
              <Typography.Span local>Add</Typography.Span>
            </Button>
          </div>

          {(filterOnlyToolbar || chipsBelow) && chipCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {renderChips()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
